import type { VoidFunctionComponent } from 'react';
import TeamRoleSelector from '../../../TeamRoleSelector';
import { useUpdateUserTeamRoles } from '../../../../../../../hooks/api';
import { useAddToast } from '../../../../../../../contexts/ToastContext';
import { getErrorMessage } from '../../../../../../../ReactQuery';

type Props = {
  userId: number;
  teamGroupId: number;
  roleId?: number;
};

/**
 * On each row of the TeamGroupMembersTable, this dropdown allows the current user
 * to change the role of the member quickly.
 *
 * @param {Props} props - The component props.
 * @param {number} props.userId - The ID of the user to change the role for.
 * @param {number} props.teamGroupId - The ID of the team group to change the role for.
 * @param {number} props.roleId - The ID of the role to select.
 */
const TeamGroupMembersTableRoleDropdown: VoidFunctionComponent<Props> = ({ userId, teamGroupId, roleId }) => {
  const addToast = useAddToast();
  const { mutateAsync: updateUserTeamRoles } = useUpdateUserTeamRoles(teamGroupId);

  const handleChangeRole = async (_userId: number, updatedRoleId: number) => {
    try {
      await updateUserTeamRoles({
        userId,
        newRoles: [{
          teamId: teamGroupId,
          roleId: updatedRoleId,
        }],
      });
      addToast({
        type: 'success',
        content: 'The user\'s team role has been updated successfully.',
      });
    } catch (error) {
      addToast({
        type: 'error',
        content: getErrorMessage(error, 'Failed to update the user\'s team role.'),
      });
    }
  };

  return (
    <TeamRoleSelector
      userId={userId}
      roleId={roleId}
      teamId={teamGroupId}
      handleChangeRole={handleChangeRole}
    />
  );
};

export default TeamGroupMembersTableRoleDropdown;
